import { Link } from 'react-router-dom';
import { Button, Skeleton } from '@/components/ui';
import { FileText, Plus, Search } from 'lucide-react';
import { useSiteSettings, usePage } from '@/hooks';
import { useAuthStore } from '@/stores';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import DOMPurify from 'dompurify';
import { remarkWikiLink } from '@/lib/remark-wiki-link';

export function Home() {
  const { data: settings, isLoading: isLoadingSettings } = useSiteSettings();
  const { user, isAuthenticated } = useAuthStore();

  // If home_page_slug is set, redirect to that page
  const homePageSlug = settings?.home_page_slug;
  const { data: homePage, isLoading: isLoadingHomePage } = usePage(homePageSlug || '');

  // Show loading state
  if (isLoadingSettings || (homePageSlug && isLoadingHomePage)) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // If home_page_slug is set and page exists, show that page
  if (homePageSlug && homePage) {
    const sanitizedContent = DOMPurify.sanitize(homePage.content);

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{homePage.title}</h1>
          {isAuthenticated && user && (user.role === 'admin' || user.id === homePage.author_id) && (
            <Link to={`/pages/${homePage.slug}/edit`}>
              <Button variant="outline" size="sm">
                编辑首页
              </Button>
            </Link>
          )}
        </div>

        {homePage.tags && homePage.tags.length > 0 && (
          <div className="flex items-center space-x-2">
            {homePage.tags.map((tag) => (
              <Link
                key={tag.id}
                to={`/tags/${tag.name}`}
                className="px-3 py-1 bg-muted rounded-full text-sm hover:bg-muted/80 transition-colors"
              >
                {tag.name}
              </Link>
            ))}
          </div>
        )}

        <div className="prose prose-slate dark:prose-invert max-w-none">
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkWikiLink]}
              rehypePlugins={[rehypeHighlight, rehypeRaw]}
              components={{
                a: ({ href, children, className }) => {
                  if (href?.startsWith('/pages/') && className?.includes('wiki-link')) {
                    return (
                      <Link to={href} className="text-primary hover:underline font-medium">
                        {children}
                      </Link>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {children}
                    </a>
                  );
                },
              }}
            >
              {sanitizedContent}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    );
  }

  // Default home page
  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <section className="text-center py-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          {settings?.site_name ? (
            <>
              {settings.logo_url && (
                <img
                  src={settings.logo_url}
                  alt={settings.site_name}
                  className="h-16 w-16 mx-auto mb-4 rounded-lg"
                />
              )}
              {settings.site_name}
            </>
          ) : (
            <>
              轻量级 <span className="text-primary">Wiki</span> 系统
            </>
          )}
        </h1>
        <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
          {settings?.site_description || '基于 Cloudflare Workers 构建的高性能 Wiki 应用，支持 Markdown 编辑、全文搜索、版本历史追踪'}
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/pages">
            <Button size="lg">
              <FileText className="h-5 w-5 mr-2" />
              浏览页面
            </Button>
          </Link>
          <Link to="/search">
            <Button variant="outline" size="lg">
              <Search className="h-5 w-5 mr-2" />
              搜索内容
            </Button>
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="grid md:grid-cols-3 gap-6">
        <div className="p-6 rounded-lg border bg-card">
          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Markdown 编辑</h3>
          <p className="text-muted-foreground">
            支持 GitHub 风格 Markdown，实时预览，代码高亮，数学公式渲染
          </p>
        </div>

        <div className="p-6 rounded-lg border bg-card">
          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
            <Search className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-2">全文搜索</h3>
          <p className="text-muted-foreground">
            基于 D1 FTS5 的高效全文搜索，支持中英文分词和关键词高亮
          </p>
        </div>

        <div className="p-6 rounded-lg border bg-card">
          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
            <Plus className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-2">版本历史</h3>
          <p className="text-muted-foreground">
            自动记录每次编辑，支持版本对比和一键回滚，永不丢失内容
          </p>
        </div>
      </section>

      {/* Getting Started */}
      {!isAuthenticated && (
        <section className="text-center py-8">
          <h2 className="text-2xl font-bold mb-4">开始使用</h2>
          <p className="text-muted-foreground mb-4">
            注册账户后即可创建和编辑页面
          </p>
          <Link to="/register">
            <Button size="lg">立即注册</Button>
          </Link>
        </section>
      )}
    </div>
  );
}
